import React, { useState, useRef, useEffect } from "react";
import { Button, Form } from "reactstrap";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";

import logo from "../../assets/img/Logo-png.svg";
import { verifyOtp, adminforgotpassword } from "../../api/ApiServices";

const VerifyOtp = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [email, setEmail] = useState(location.state?.email || "");
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const inputRefs = useRef([]);

  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [countdown, setCountdown] = useState(30);

  useEffect(() => {
    // Auto focus first input box
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleDigitChange = (index, value) => {
    const cleanValue = value.replace(/\D/g, "");
    if (!cleanValue) {
      const newDigits = [...digits];
      newDigits[index] = "";
      setDigits(newDigits);
      return;
    }

    // Single digit input
    const lastChar = cleanValue.slice(-1);
    const newDigits = [...digits];
    newDigits[index] = lastChar;
    setDigits(newDigits);
    setErrorMsg("");

    // Auto focus next box
    if (index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;

    const newDigits = [...digits];
    for (let i = 0; i < 6; i++) {
      newDigits[i] = pasted[i] || "";
    }
    setDigits(newDigits);
    setErrorMsg("");

    const nextIndex = Math.min(pasted.length, 5);
    inputRefs.current[nextIndex]?.focus();
  };

  const handleResendOtp = async () => {
    if (!email.trim()) {
      setErrorMsg("Email address is missing. Please go back.");
      return;
    }

    try {
      setResending(true);
      setErrorMsg("");
      const res = await adminforgotpassword({ email: email.trim() });
      if (res?.success) {
        setCountdown(45);
        await Swal.fire({
          icon: "success",
          title: "OTP Resent!",
          text: "A fresh 6-digit OTP has been sent to your email.",
          confirmButtonColor: "#16a34a",
          confirmButtonText: "OK",
        });
      }
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || "Failed to resend OTP. Please try again.");
    } finally {
      setResending(false);
    }
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    const fullOtp = digits.join("");
    setErrorMsg("");
    setSuccessMsg("");

    if (!email.trim()) {
      setErrorMsg("Email address is required.");
      return;
    }

    if (fullOtp.length !== 6) {
      setErrorMsg("Please enter all 6 digits of your OTP code.");
      return;
    }

    try {
      setLoading(true);

      const response = await verifyOtp(email.trim(), fullOtp.trim());

      if (response?.success) {
        // Direct redirect without popup
        navigate("/auth/set-new-password", {
          state: {
            email: email.trim(),
          },
        });
        return;
      } else {
        setErrorMsg(response?.message || "Invalid OTP code entered.");
      }
    } catch (error) {
      console.error("Verify OTP Error:", error);
      setErrorMsg(
        error?.response?.data?.message ||
        error?.message ||
        "Invalid OTP. Please check the code and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-card">
      {/* Header with Logo and Step Badge */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <img src={logo} alt="Area Trek" style={{ height: "36px", maxWidth: "130px", objectFit: "contain" }} />
        <span
          style={{
            background: "#f0fdf4",
            color: "#16a34a",
            fontSize: "0.75rem",
            fontWeight: "700",
            padding: "4px 12px",
            borderRadius: "20px",
            border: "1px solid #bbf7d0",
            whiteSpace: "nowrap",
          }}
        >
          STEP 2 / 3
        </span>
      </div>

      {/* Shield Emblem */}
      <div className="text-center my-3">
        <div
          style={{
            width: "56px",
            height: "56px",
            borderRadius: "16px",
            background: "#f0fdf4",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#16a34a",
            fontSize: "1.4rem",
            border: "1px solid #86efac",
          }}
        >
          <i className="fas fa-shield-alt"></i>
        </div>
      </div>

      <h2
        className="text-center font-weight-bold mb-1"
        style={{ color: "#111827", fontSize: "clamp(1.2rem, 4vw, 1.45rem)" }}
      >
        Verify 6-Digit Code
      </h2>

      {/* Email Chip */}
      <div className="text-center mb-4">
        <div
          style={{
            display: "inline-block",
            background: "#f3f4f6",
            padding: "6px 14px",
            borderRadius: "20px",
            fontSize: "0.82rem",
            color: "#4b5563",
            fontWeight: "500",
            maxWidth: "100%",
            wordBreak: "break-word",
          }}
        >
          Code sent to: <strong style={{ color: "#111827" }}>{email || "your email"}</strong>
        </div>
      </div>

      <Form onSubmit={handleSubmit}>
        {/* 6 INDIVIDUAL OTP INPUT BOXES */}
        <div className="mb-4" onPaste={handlePaste}>
          <div className="otp-inputs-container">
            {digits.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                value={digit}
                onChange={(e) => handleDigitChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                disabled={loading}
                className={`otp-digit-input ${digit ? "filled" : ""} ${
                  errorMsg && !digit ? "has-error" : ""
                }`}
              />
            ))}
          </div>

          {errorMsg && (
            <div
              className="text-center mt-3"
              style={{
                color: "#f5365c",
                fontSize: "12px",
                fontWeight: "500",
              }}
            >
              <i className="fas fa-exclamation-circle mr-1"></i> {errorMsg}
            </div>
          )}
        </div>

        {/* Resend Action Row */}
        <div className="d-flex justify-content-between align-items-center mb-4 px-1 flex-wrap" style={{ gap: "4px" }}>
          <span style={{ fontSize: "0.82rem", color: "#6b7280" }}>
            Didn't receive code?
          </span>

          {countdown > 0 ? (
            <span style={{ fontSize: "0.82rem", color: "#9ca3af", fontWeight: "600" }}>
              Resend in <strong style={{ color: "#16a34a" }}>{countdown}s</strong>
            </span>
          ) : (
            <button
              type="button"
              onClick={handleResendOtp}
              disabled={resending || loading}
              style={{
                background: "transparent",
                border: "none",
                color: "#16a34a",
                fontWeight: "700",
                fontSize: "0.82rem",
                cursor: "pointer",
                padding: "0",
              }}
            >
              {resending ? "Sending..." : "Resend Code"}
            </button>
          )}
        </div>

        <Button
          className="sign-in-btn"
          type="submit"
          disabled={loading || digits.join("").length !== 6}
          style={{
            background: "linear-gradient(90deg, #16a34a 0%, #15803d 100%)",
          }}
        >
          {loading ? (
            <>
              <i className="fas fa-spinner fa-spin mr-2 gradient-color-loader"></i>
              Verifying Code...
            </>
          ) : (
            <>
              Verify & Continue
              <i className="fas fa-arrow-right ml-2"></i>
            </>
          )}
        </Button>

        <div className="text-center mt-4 pt-2">
          <a
            href="/auth/forgot-password"
            onClick={(e) => {
              e.preventDefault();
              navigate("/auth/forgot-password");
            }}
            style={{
              color: "#6b7280",
              fontSize: "0.85rem",
              fontWeight: "600",
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            <i className="fas fa-arrow-left mr-2" style={{ fontSize: "0.75rem" }}></i> Change Email Address
          </a>
        </div>
      </Form>
    </div>
  );
};

export default VerifyOtp;